import { Form, TabsProps } from 'antd'
import { Box, Button, Stack } from '@mui/material'
import { useEffect, useState } from 'react'
import CreateTabs from '../../Components/CreateTabs'
import { CREATE_COHORT } from '../../Graphql/Mutations'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Error } from '../../Types/MessageType'
import globals from '../../Constants/Globals'
import LoadingButton from '@mui/lab/LoadingButton'
import Message from '../../Components/Message'
import { useLocation, useNavigate } from 'react-router-dom'
import CreateCohortForm from '../../Components/SubPages/CreateCohortForm'
import Users from '../SuperAdmin/Users'
import Trainings from '../SuperAdmin/Trainings'
import { useSelector } from 'react-redux'
import { GET_COHORT_BY_ID, GET_USERS_BY_ORG_ID } from '../../Graphql/Queries'
import { AdminPaths } from '../../Routes/Paths'
import useUserPermissions from '../../hooks/useUserPermission'
import { AdminPermissions } from '../../Constants/Permissions'

const { COHORT_PATH } = AdminPaths

const CreateCohort = () => {
  const [tab, setTab] = useState<boolean>(true)
  const [selectedKey, setSelectedKey] = useState<string>('1')
  const [form] = Form.useForm()
  const [alert, setAlert] = useState<Error>({ type: 'warning', message: '' })
  const [createCohort, { loading: loadingCohort }] = useMutation(CREATE_COHORT)
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedOrg, setSelectedOrg] = useState<string | null>(null)
  const [filteredUsers, setFilteredUsers] = useState<any[]>([])
  const [selectedUsers, setSelectedUsers] = useState<any[]>(
    form.getFieldValue('userIds') || []
  )

  const { VIEW_UPDATE_COHORT, VIEW_SAVE_COHORT } = AdminPermissions
  const { permissions } = useUserPermissions()

  // query to get cohort details if updating
  const [getCohortById] = useLazyQuery(GET_COHORT_BY_ID, {
    variables: {
      getCohortByIdId: location.state?.cohortId,
    },
    onCompleted(data) {
      const getCohortId = data.getCohortById;
  
      form.setFieldsValue({
        ...getCohortId,
        organization: getCohortId.orgId,
      });
  
      setSelectedOrg(getCohortId.orgId);
      setSelectedUsers(getCohortId.userIds || []);
    },
  });

  useEffect(() => {
    if (!!location.state?.cohortId) {
      getCohortById()
    }
  }, [location.state?.cohortId, getCohortById])

  useEffect(() => {
    if (selectedOrg) {
      handleOrgChange(selectedOrg);
    }
  }, [selectedOrg]);  

  // Lazy query to get users by organization ID
  const [getUsersByOrgId, { data: usersData, loading: usersLoading }] =
    useLazyQuery(GET_USERS_BY_ORG_ID, {
      onCompleted(data) {
        // Debugging: Log the fetched users and selected users
        console.log("Fetched Users:", data.getUsersByOrgId);
        console.log("Selected Users:", selectedUsers);

        // Extract IDs from selectedUsers (ensure selectedUsers is an array of strings)
        const selectedUserIds = Array.isArray(selectedUsers) ? selectedUsers : [];
        console.log("Selected User IDs:", selectedUserIds);

        // Sort users: selected users first
        const sortedUsers = data.getUsersByOrgId.sort((a: any, b: any) => {
          const aId = a.id || a._id; // Adjust based on the ID field in fetched users
          const bId = b.id || b._id; // Adjust based on the ID field in fetched users

          const isASelected = selectedUserIds.includes(aId);
          const isBSelected = selectedUserIds.includes(bId);

          return Number(isBSelected) - Number(isASelected); // Selected users appear first
        });

        // Debugging: Log the sorted users
        console.log("Sorted Users:", sortedUsers);

        setFilteredUsers(sortedUsers);
      },
      onError(error) {
        console.error('Error fetching users:', error.message);
      },
    })

  useEffect(() => {
    if (selectedOrg && selectedKey === '2') {
      getUsersByOrgId({ variables: { orgId: selectedOrg } })
    }
  }, [selectedOrg, selectedKey, getUsersByOrgId])

  // Handle organization selection
  const handleOrgChange = (orgId: string) => {
    setSelectedOrg(orgId)
    form.setFieldsValue({ organization: orgId })
  }

  // Handle checkbox change
  const handleCheckboxChange = (userId: string) => {
    setSelectedUsers((prevSelectedUsers) =>
      prevSelectedUsers.includes(userId)
        ? prevSelectedUsers.filter((id) => id !== userId)
        : [...prevSelectedUsers, userId]
    )
    form.setFieldValue('userIds', selectedUsers)
  }

  // Define the tabs (Cohort Details, User Details, Training Details)
  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <h3>Batch Details</h3>,
      children: (
        <CreateCohortForm
          alertMessage={alert}
          currentLocation={!!location.state?.cohortId}
          onOrgChange={handleOrgChange}
          selectedOrgId={form.getFieldValue('organization')}
        />
      ),
      disabled: false,  
    },
    {
      key: '2',
      label: <h3>User Details</h3>,
      children: (
        <Form.Item name={'userIds'}>
          <Users
            showViewDetails={false}
            showCreateUser={false}
            check={true}
            tableFormItemName={'userIds'}
            formdata={selectedUsers}
            users={filteredUsers}
            selectedUsers={selectedUsers}
            onCheckboxChange={handleCheckboxChange}
          />
        </Form.Item>
      ),
      disabled: false, 
    },
  ]

  const handleTabChange = (isSelected: boolean, tabKey: string) => {
    if (isSelected && selectedKey !== tabKey) {
      setSelectedKey(tabKey);
    }
  };
  
  const handleTabChangeWrapper = (isSelected: boolean, tabKey: string) => {
    handleTabChange(isSelected, tabKey);
  };

  // Submit handler
  const createCohortHandler = (value: any) => {
    let cohortFormData
    if (!!location.state?.cohortId) {
      cohortFormData = { ...value, _id: location.state?.cohortId }
    } else {
      cohortFormData = { ...value }
    }

    createCohort({
      variables: {
        cohortInput: cohortFormData,
      },
      onCompleted() {
        const successMessage = location.state?.cohortId
          ? 'Batch updated successfully'
          : 'Batch created successfully'
        setAlert({ type: 'success', message: successMessage })
        form.resetFields()
        setTimeout(() => {
          navigate(COHORT_PATH)
          setAlert({ type: 'success', message: '' })
        }, 3000)
      },
      onError(error) {
        setAlert({ type: 'error', message: error.message })
        setTimeout(() => {
          setAlert({ type: 'error', message: '' })
        }, globals.timeout)
      },
    })
  }

  // Buttons for navigation
  const showContinueButton = (
    <Button
      onClick={() => {
        form.validateFields()
          .then(() => {
            setSelectedKey('2')
            setTab(false)
            getUsersByOrgId({ variables: { orgId: selectedOrg } })
          })
          .catch((errorInfo) => {
            console.log('Validation failed:', errorInfo)
          })
      }}
      sx={{ px: 5, py: 1, color: 'white', height: '45px' }}
      variant='contained'
    >
      Continue
    </Button>
  )

  const showSaveButton = (
    <Stack direction={'row'} spacing={2}>
      <Button
        sx={{ px: 5, py: 1, height: '45px', color: 'white' }}
        variant='contained'
        onClick={() => {
          setSelectedKey('1')
          setTab(false)
        }} // Go back to Batch Details
      >
        Back
      </Button>
      <Button
        sx={{ px: 5, py: 1, color: 'white', height: '45px' }}
        variant='contained'
        type='submit'
      >
        {location.state?.cohortId ? 'Update' : 'Save'}
      </Button>
    </Stack>
  )

  return (
    <>
      <Form
        onFinish={createCohortHandler}
        form={form}
        layout={'vertical'}
        initialValues={{
          userIds: selectedUsers,
          cohortTrainings: form.getFieldValue('cohortTrainings'),
        }}
      >
        <Box sx={{ mb: 3 }} display={'flex'} justifyContent={'flex-end'}>
          <Box flexGrow={1} sx={{ pr: 3 }}>
            {!!alert?.message ? (
              <Message
                type={alert?.type}
                message={alert.message}
                variant={'filled'}
              />
            ) : null}
          </Box>
          {selectedKey !== '2' ? showContinueButton : showSaveButton}
        </Box>
        <CreateTabs
          items={items}
          heading={'Create Batch'}
          headingStyle={{ color: 'black' }}
          keySelected={selectedKey}
          defaultKey={'1'}
          selectedTab={(isSelected: boolean, tabKey: string) => handleTabChangeWrapper(isSelected, tabKey)}

        />
      </Form>
    </>
  )
}

export default CreateCohort
